import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Lock, Play, Clock, ShoppingBag, Eye, EyeOff, AlertCircle, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import MuxPlayer from "@mux/mux-player-react";
import Footer from "../components/Footer";

// Store links; hidden while the public Video Store is off.
const SHOW_STORE_LINKS = false;

export default function MyLocker() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [emailInput, setEmailInput] = useState("");
  const [showEmail, setShowEmail] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [playingVideo, setPlayingVideo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Auto-authenticate if email in URL
    if (email) {
      setEmailInput(email);
      loadPurchases(email);
    }
  }, []);

  const loadPurchases = async (customerEmail) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/store/purchases?email=${encodeURIComponent(customerEmail)}`
      );
      const data = await res.json();
      
      if (res.ok) {
        setPurchases(data.purchases || []);
        setAuthenticated(true);
        setEmail(customerEmail);
        toast.success(`✓ Loaded ${data.purchases?.length || 0} purchase(s)`);
      } else {
        toast.error("Failed to load purchases");
      }
    } catch (e) {
      console.error("Failed to load purchases:", e);
      toast.error("Failed to load purchases");
    } finally {
      setLoading(false);
    }
  };

  const handleAccessLocker = async (e) => {
    e.preventDefault();
    if (!emailInput.trim()) {
      toast.error("Please enter your email");
      return;
    }
    await loadPurchases(emailInput);
  };

  const playVideo = async (purchase) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/store/access/${purchase.video_id}?email=${encodeURIComponent(email)}`
      );
      const data = await res.json();
      
      if (res.ok && data.has_access) {
        setPlayingVideo(data);
        toast.success("✓ Video ready to play");
      } else {
        if (data.reason === "expired") {
          toast.error("Your rental has expired");
        } else {
          toast.error("Access denied");
        }
      }
    } catch (e) {
      console.error("Failed to get video access:", e);
      toast.error("Failed to load video");
    } finally {
      setLoading(false);
    }
  };

  const closePlayer = () => {
    setPlayingVideo(null);
  };

  // Calculate expiration status
  const getExpirationStatus = (purchase) => {
    if (purchase.purchase_type === "direct_sale") {
      return { text: "Lifetime Access", color: "text-green-400", badge: "bg-green-500/20 border-green-500/30" };
    }
    
    if (purchase.expires_at) {
      const expiresAt = new Date(purchase.expires_at);
      const now = new Date();
      const hoursLeft = Math.max(0, (expiresAt - now) / (1000 * 60 * 60));
      
      if (hoursLeft <= 0) {
        return { text: "Expired", color: "text-red-400", badge: "bg-red-500/20 border-red-500/30" };
      } else if (hoursLeft < 24) {
        return { 
          text: `${Math.floor(hoursLeft)}h left`, 
          color: "text-yellow-400", 
          badge: "bg-yellow-500/20 border-yellow-500/30" 
        };
      } else {
        const daysLeft = Math.floor(hoursLeft / 24);
        return { 
          text: `${daysLeft}d left`, 
          color: "text-blue-400", 
          badge: "bg-blue-500/20 border-blue-500/30" 
        };
      }
    }
    
    return { text: "Active", color: "text-green-400", badge: "bg-green-500/20 border-green-500/30" };
  };

  // Login/Auth Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--gio-bg)" }}>
        <div 
          className="w-full max-w-md p-8 rounded-2xl border"
          style={{ background: "var(--gio-surface)", borderColor: "var(--gio-border)" }}
        >
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" 
              style={{ background: "var(--gio-primary)", opacity: 0.1 }}>
              <Lock size={32} style={{ color: "var(--gio-primary)" }} />
            </div>
            <h1 className="text-3xl font-heading mb-2" style={{ color: "var(--gio-text)" }}>
              My Locker
            </h1>
            <p className="text-sm" style={{ color: "var(--gio-muted)" }}>
              Access your purchased videos securely
            </p>
          </div>

          <form onSubmit={handleAccessLocker} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: "var(--gio-text)" }}>
                Email Address
              </label>
              <div className="relative">
                <input
                  type={showEmail ? "text" : "email"}
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 rounded-lg pr-12"
                  style={{
                    background: "var(--gio-bg)",
                    border: "1px solid var(--gio-border)",
                    color: "var(--gio-text)",
                  }}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowEmail(!showEmail)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-[var(--gio-surface-hi)] transition"
                  style={{ color: "var(--gio-muted)" }}
                >
                  {showEmail ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="text-xs mt-2" style={{ color: "var(--gio-muted-3)" }}>
                Enter the email you used to purchase videos
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition hover:opacity-90 disabled:opacity-50"
              style={{ background: "var(--gio-primary)", color: "#fff" }}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Lock size={18} />
                  Access My Locker
                </>
              )}
            </button>
          </form>

          {SHOW_STORE_LINKS && (
          <div className="mt-6 pt-6 border-t text-center" style={{ borderColor: "var(--gio-border)" }}>
            <button
              onClick={() => navigate("/store")}
              className="text-sm flex items-center justify-center gap-2 mx-auto hover:opacity-70 transition"
              style={{ color: "var(--gio-primary)" }}
            >
              <ShoppingBag size={16} />
              Browse Video Store
            </button>
          </div>
          )}
        </div>
      </div>
    );
  }

  // Video Player Modal
  if (playingVideo) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.95)" }}>
        <div className="w-full max-w-5xl">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-heading" style={{ color: "var(--gio-text)" }}>
                {playingVideo.video.title}
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <CheckCircle size={14} className="text-green-400" />
                <span className="text-sm text-green-400">DRM Protected Stream</span>
                <span className="text-xs" style={{ color: "var(--gio-muted-3)" }}>
                  • Token expires in 2 hours
                </span>
              </div>
            </div>
            <button
              onClick={closePlayer}
              className="px-4 py-2 rounded hover:bg-zinc-800 transition"
              style={{ color: "var(--gio-muted)" }}
            >
              Close
            </button>
          </div>

          <div className="rounded-xl overflow-hidden" style={{ background: "#000" }}>
            <MuxPlayer
              playbackId={playingVideo.video.mux_playback_id}
              tokens={{
                playback: playingVideo.video.playback_token,
              }}
              metadata={{
                video_title: playingVideo.video.title,
                viewer_user_id: playingVideo.purchase.customer_email,
              }}
              streamType="on-demand"
              controls
              autoPlay
              style={{ width: "100%", aspectRatio: "16/9" }}
            />
          </div>

          <div 
            className="mt-4 p-4 rounded-lg flex items-start gap-3"
            style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}
          >
            <AlertCircle size={20} style={{ color: "var(--gio-primary)", flexShrink: 0 }} />
            <div className="text-sm" style={{ color: "var(--gio-muted)" }}>
              <strong style={{ color: "var(--gio-text)" }}>Protected Content:</strong> This video is protected by DRM watermarking. 
              Unauthorized recording, screen capture, or redistribution is prohibited and may result in legal action.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Purchases Grid
  return (
    <div className="min-h-screen" style={{ background: "var(--gio-bg)" }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: "var(--gio-border)" }}>
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Lock size={32} style={{ color: "var(--gio-primary)" }} />
                <h1 className="text-4xl font-heading" style={{ color: "var(--gio-text)" }}>
                  My Locker
                </h1>
              </div>
              <p className="text-sm" style={{ color: "var(--gio-muted)" }}>
                Signed in as: <span className="font-mono">{email}</span>
              </p>
            </div>
            {SHOW_STORE_LINKS && (
            <button
              onClick={() => navigate("/store")}
              className="px-4 py-2 rounded-lg flex items-center gap-2 transition hover:opacity-80"
              style={{ background: "var(--gio-primary)", color: "#fff" }}
            >
              <ShoppingBag size={18} />
              Browse Store
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Purchases Grid */}
      <div className="container mx-auto px-6 py-12">
        {purchases.length === 0 ? (
          <div 
            className="text-center py-16 rounded-lg"
            style={{ background: "var(--gio-surface)", border: "1px solid var(--gio-border)" }}
          >
            <Lock size={64} className="mx-auto mb-4 opacity-20" />
            <h2 className="text-2xl font-heading mb-2" style={{ color: "var(--gio-text)" }}>
              No Purchases Yet
            </h2>
            <p className="mb-6" style={{ color: "var(--gio-muted)" }}>
              Your purchased videos will appear here
            </p>
            {SHOW_STORE_LINKS && (
            <button
              onClick={() => navigate("/store")}
              className="px-6 py-3 rounded-lg font-semibold inline-flex items-center gap-2"
              style={{ background: "var(--gio-primary)", color: "#fff" }}
            >
              <ShoppingBag size={18} />
              Browse Video Store
            </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {purchases.map((purchase) => {
              const status = getExpirationStatus(purchase);
              const canPlay = status.text !== "Expired";

              return (
                <div
                  key={purchase.id}
                  className="rounded-xl overflow-hidden border transition hover:shadow-2xl"
                  style={{ 
                    background: "var(--gio-surface)", 
                    borderColor: "var(--gio-border)",
                    opacity: canPlay ? 1 : 0.6
                  }}
                >
                  {/* Video info will be fetched when playing */}
                  <div className="aspect-video bg-zinc-900 flex items-center justify-center relative">
                    <Play size={48} className="opacity-30" />
                    <div 
                      className={`absolute top-2 right-2 px-2 py-1 rounded text-xs font-bold border ${status.badge}`}
                    >
                      <span className={status.color}>{status.text}</span>
                    </div>
                  </div>

                  <div className="p-4">
                    <h3 className="font-heading text-lg mb-2" style={{ color: "var(--gio-text)" }}>
                      Video #{purchase.video_id.slice(0, 8)}
                    </h3>
                    
                    <div className="flex items-center gap-2 text-sm mb-3" style={{ color: "var(--gio-muted)" }}>
                      <Clock size={14} />
                      {new Date(purchase.purchased_at).toLocaleDateString()}
                    </div>

                    <div className="flex items-center justify-between text-sm mb-4">
                      <span style={{ color: "var(--gio-muted)" }}>
                        {purchase.purchase_type === "direct_sale" ? "Owned" : "Rental"}
                      </span>
                      <span className="font-bold" style={{ color: "var(--gio-primary)" }}>
                        ${purchase.price_paid.toFixed(2)}
                      </span>
                    </div>

                    <button
                      onClick={() => canPlay && playVideo(purchase)}
                      disabled={!canPlay || loading}
                      className="w-full py-2.5 rounded-lg font-semibold flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
                      style={{ 
                        background: canPlay ? "var(--gio-primary)" : "var(--gio-surface-hi)", 
                        color: "#fff" 
                      }}
                    >
                      <Play size={16} />
                      {canPlay ? "Watch Now" : "Expired"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
